import loadStatsBasketball, {
	type BasketballStats,
} from "./loadStats.basketball.ts";
import { PHASE, PLAYER, REAL_PLAYERS_INFO } from "../../../common/constants.ts";
import type {
	GetLeagueOptions,
	NonEmptyArray,
	PlayerAward,
	PlayerContract,
	PlayerInjury,
} from "../../../common/types.ts";
import getOnlyRatings, { type OnlyRatings } from "./getOnlyRatings.ts";
import type { Basketball, Ratings } from "./loadData.basketball.ts";
import nerfDraftProspect from "./nerfDraftProspect.ts";
import oldAbbrevTo2020BBGMAbbrev from "./oldAbbrevTo2020BBGMAbbrev.ts";
import setDraftProspectRatingsBasedOnDraftPosition from "./setDraftProspectRatingsBasedOnDraftPosition.ts";
import { getEWA } from "../../util/advStats.basketball.ts";
import { averageSalary } from "./averageSalary.ts";
import { helpers } from "../../util/index.ts";
import {
	formatPlayerAward,
	getDefaultAwardsByShortName,
} from "./formatPlayerAward.ts";

const MINUTES_PER_GAME = 48;

const hasPhoto = ["seemebo01"];

export const getDraftTid = (
	teams: {
		srID?: string;
		tid: number;
	}[],
	draftAbbrev: string,
) => {
	const draftTeam = teams.find(
		(t) =>
			t.srID !== undefined && oldAbbrevTo2020BBGMAbbrev(t.srID) === draftAbbrev,
	);
	if (draftTeam) {
		return draftTeam.tid;
	} else {
		return -1;
	}
};

const formatPlayerFactory = async (
	basketball: Basketball,
	options: GetLeagueOptions,
	season: number,
	teams: {
		tid: number;
		srID?: string;
	}[],
	initialPid: number,
) => {
	let pid = initialPid;

	let basketballStats: BasketballStats | undefined;
	if (options.type === "real" && options.realStats !== "none") {
		basketballStats = await loadStatsBasketball();
	}

	const defaultAwardsByShortName = getDefaultAwardsByShortName();

	const tidCache: Record<string, number | undefined> = {};
	const getTidNormal = (abbrev?: string): number | undefined => {
		if (abbrev === undefined) {
			return;
		}
		if (Object.hasOwn(tidCache, abbrev)) {
			return tidCache[abbrev];
		}

		const t = teams.find(
			(t) =>
				t.srID !== undefined && oldAbbrevTo2020BBGMAbbrev(t.srID) === abbrev,
		);

		const tid = t?.tid;

		tidCache[abbrev] = tid;

		return tid;
	};

	return (
		ratingsInput: Ratings[] | Ratings,
		{
			draftProspect,
			hasQueens,
		}: {
			draftProspect?: boolean;
			hasQueens?: boolean;
		} = {},
	) => {
		const allRatings = Array.isArray(ratingsInput)
			? ratingsInput
			: [ratingsInput];

		const ratings = allRatings.at(-1)!;

		const slug = ratings.slug;

		const bio = basketball.bios[slug];
		if (!bio) {
			throw new Error(`No bio found for "${slug}"`);
		}

		const teamsRows = basketball.teams[slug];

		// For alexnoob draft prospects who already have their draft ratings set for the correct season, as opposed to other rookies who need them set based on their rookie ratings
		const draftRatingsAlreadySet = bio.draftYear === ratings.season;

		const legends = options.type === "legends";

		let draft;
		if (draftProspect || legends) {
			draft = {
				tid: -1,
				originalTid: -1,
				round: 0,
				pick: 0,
				year: legends
					? season - 1
					: draftRatingsAlreadySet
						? ratings.season
						: ratings.season - 1,
			};
		} else {
			// Same as getDraftTid, but cached
			const draftTid = getTidNormal(bio.draftAbbrev) ?? -1;
			draft = {
				tid: draftTid,
				originalTid: draftTid,
				round: bio.draftRound,
				pick: bio.draftPick,
				year: bio.draftYear,
			};
		}

		let tid: number;
		let jerseyNumber: string | undefined;
		if (ratings.retiredUntil !== undefined) {
			tid = PLAYER.RETIRED;
		} else if (draftProspect) {
			tid = PLAYER.UNDRAFTED;
		} else if (!legends && ratings.season < season) {
			tid = PLAYER.RETIRED;
		} else {
			tid = PLAYER.FREE_AGENT;
			let statsRow;
			if (teamsRows) {
				if (options.type === "real" && options.phase >= PHASE.PLAYOFFS) {
					// Search backwards - last team a player was on that season
					for (let i = teamsRows.length - 1; i >= 0; i--) {
						const row = teamsRows[i]!;
						if (
							row.season === ratings.season &&
							(row.phase === undefined || options.phase >= row.phase)
						) {
							statsRow = row;
							break;
						}
					}
				} else {
					// Search forwards - first team a player was on that season
					statsRow = teamsRows.find((row) => row.season === ratings.season);
				}
			}
			const abbrev = statsRow ? statsRow.abbrev : ratings.abbrev_if_new_row;

			if (statsRow) {
				jerseyNumber = statsRow.jerseyNumber;
			}

			if (legends) {
				const team = teams.find((t) => {
					if (hasQueens && abbrev === "NOL" && ratings.season < 2003) {
						return (
							t.srID !== undefined &&
							oldAbbrevTo2020BBGMAbbrev(t.srID) === "CHA"
						);
					}

					return (
						t.srID !== undefined && oldAbbrevTo2020BBGMAbbrev(t.srID) === abbrev
					);
				});
				tid = team ? team.tid : PLAYER.FREE_AGENT;
			} else {
				const newTid =
					typeof abbrev === "number" ? abbrev : getTidNormal(abbrev);
				if (newTid !== undefined) {
					tid = newTid;
				}
			}
		}

		if (jerseyNumber === undefined && tid !== PLAYER.RETIRED) {
			// Fallback (mostly for draft prospects) - pick first number in database
			const statsRow2 = teamsRows?.[0];
			if (statsRow2) {
				jerseyNumber = statsRow2.jerseyNumber;
			}
		}

		if (tid >= PLAYER.FREE_AGENT && !draftProspect) {
			// Ensure draft year is before the current season, because for some players like Irv Rothenberg this is not true
			if (
				draft.year > season ||
				(draft.year === season &&
					(options.type === "legends" || options.phase <= PHASE.DRAFT))
			) {
				draft = {
					tid: -1,
					originalTid: -1,
					round: 0,
					pick: 0,
					year: season - 1,
				};
			}
		}

		let contract: PlayerContract | undefined;
		let awards: PlayerAward[] | undefined;
		let salaries;
		if (options.type === "legends") {
			contract = {
				amount: 6000,
				exp: season + 3,
			};
		} else if (!options.randomDebuts || options.randomDebutsKeepCurrent) {
			const salaryRows = basketball.salaries[slug]?.filter((row) => {
				// Auto-apply extensions, otherwise will feel weird
				if (season >= REAL_PLAYERS_INFO!.MAX_SEASON) {
					return true;
				}

				return row.start <= season;
			});

			if (salaryRows && salaryRows.length > 0 && !draftProspect) {
				// Complicated stuff rather than just taking last entry because these can be out of order, particularly due to merging data sources. But still search backwards
				let salaryRow = salaryRows.findLast(
					(row) => row.start <= season && row.exp >= season,
				);
				if (season >= REAL_PLAYERS_INFO!.MAX_SEASON) {
					// Auto-apply extensions, otherwise will feel weird
					const salaryRowExtension = salaryRows.find(
						(row) => row.start > season,
					);
					if (salaryRowExtension) {
						salaryRow = salaryRowExtension;
					}
				}

				if (salaryRow) {
					// Bound at 5 year contract
					const exp = Math.min(salaryRow.exp, season + 4);
					contract = {
						amount: averageSalary(salaryRow, season, exp),
						exp,
					};

					if (salaryRow.start === draft.year + 1) {
						contract.rookie = true;
					}
				}

				const maxSalaryHistorySeason = contract?.exp ?? season - 1;
				salaries = [];
				for (const row of salaryRows) {
					const maxSeason = Math.min(row.exp, maxSalaryHistorySeason);

					for (let season2 = row.start; season2 <= maxSeason; season2++) {
						if (contract && row === salaryRow && season2 >= season) {
							// Current contract, use averageSalary output from above for current season or later
							salaries.push({
								amount: contract.amount,
								season: season2,
							});
						} else {
							const i = season2 - row.start;

							// Historical salary, use exact value every year
							salaries.push({
								amount: helpers.roundContract(row.amounts[i]! / 1000),
								season: season2,
							});
						}
					}
				}
			}

			const allAwards = basketball.awards[slug];

			const awardsCutoffSeason =
				options.type === "real" && options.phase > PHASE.PLAYOFFS
					? season + 1
					: season;

			awards =
				allAwards && !draftProspect
					? helpers
							.deepCopy(
								allAwards.filter(
									(award) =>
										award.season < awardsCutoffSeason ||
										(options.type === "real" &&
											options.phase === PHASE.PLAYOFFS &&
											award.season < awardsCutoffSeason + 1 &&
											((award.type !== undefined &&
												award.type.includes("All-Star")) ||
												award.type === "Slam Dunk Contest Winner" ||
												award.type === "Three-Point Contest Winner")),
								),
							)
							.map((award) => {
								if (award.type !== undefined) {
									return award;
								}

								return formatPlayerAward(award, defaultAwardsByShortName);
							})
					: undefined;
		}

		let bornYear;
		if (legends) {
			const age = ratings.season - bio.bornYear;
			bornYear = season - age;
		} else {
			bornYear = bio.bornYear;
		}

		// Whitelist, to get rid of any other columns
		const processedRatings = allRatings.map((row) =>
			getOnlyRatings(row, true),
		) as [OnlyRatings, ...OnlyRatings[]];

		const addDummyRookieRatings =
			!draftProspect &&
			options.type === "real" &&
			(options.realStats === "all" ||
				options.realStats === "allActive" ||
				options.realStats === "allActiveHOF") &&
			processedRatings[0].season !== draft.year;
		if (addDummyRookieRatings) {
			processedRatings.unshift({
				...processedRatings[0],
				season: draft.year,
			});
		}

		if (draftProspect || addDummyRookieRatings) {
			const currentRatings = processedRatings[0];

			if (!draftRatingsAlreadySet) {
				nerfDraftProspect(currentRatings);
			}

			if (options.type === "real" && options.realDraftRatings === "draft") {
				const age = currentRatings.season! - bornYear;
				setDraftProspectRatingsBasedOnDraftPosition(currentRatings, age, bio);
			}
		}

		const name = legends ? `${bio.name} ${ratings.season}` : bio.name;

		type StatsRow = Omit<
			BasketballStats["stats"][string][number],
			"slug" | "abbrev" | "playoffs"
		> & {
			playoffs: boolean;
			tid: number;
			minAvailable: number;
			ewa?: number;
			td?: number;
			qd?: number;
			fxf?: number;
			trb?: number;
		};
		let stats: StatsRow[] | undefined;
		if (options.type === "real" && basketballStats) {
			const statsRows = basketballStats.stats[slug];
			if (statsRows) {
				let statsTemp: BasketballStats["stats"][string] | undefined;

				const statsSeason =
					options.phase > PHASE.REGULAR_SEASON
						? options.season
						: options.season - 1;
				const includePlayoffs = options.phase !== PHASE.PLAYOFFS;

				if (options.realStats === "lastSeason") {
					statsTemp = statsRows.filter(
						(row) =>
							row.season === statsSeason && (includePlayoffs || !row.playoffs),
					);
				} else if (
					options.realStats === "allActiveHOF" ||
					options.realStats === "allActive" ||
					options.realStats === "all"
				) {
					statsTemp = statsRows.filter(
						(row) =>
							row.season <= statsSeason &&
							(includePlayoffs || !row.playoffs || row.season < statsSeason),
					);
				}

				if (statsTemp && statsTemp.length > 0) {
					stats = statsTemp.map((row) => {
						let tid = getTidNormal(row.abbrev);
						if (tid === undefined) {
							// Team was disbanded
							tid = PLAYER.DOES_NOT_EXIST;
						}

						// Use rest rather than delete to remove abbrev, because delete makes objects slow in V8
						const { abbrev: _abbrev, ...rowWithoutAbbrev } = row;
						const newRow: StatsRow = {
							...rowWithoutAbbrev,
							playoffs: !!row.playoffs,
							tid,
							minAvailable: (row.gp ?? 0) * MINUTES_PER_GAME,
						};

						// EWA is not in raw data, so we need to compute it when possible
						if (newRow.per !== undefined && newRow.min !== undefined) {
							newRow.ewa = getEWA(newRow.per, newRow.min, bio.pos, 1);
						}

						// Set these to explicitly 0 for seasons since the relevant stats (ast/trb, and blk/stl) were tracked
						if (newRow.season >= 1951) {
							if (newRow.qd === undefined) {
								newRow.qd = 0;
							}

							// Ideally this would be in the raw data, but it seems they use null rather than 0 for some seasons/players
							if (newRow.td === undefined) {
								newRow.td = 0;
							}
						}
						if (newRow.season >= 1975 && newRow.fxf === undefined) {
							newRow.fxf = 0;
						}

						return newRow;
					});
				}
			}
		}

		const hof: 1 | undefined =
			!!awards &&
			awards.some((award) => award.type === "Inducted into the Hall of Fame")
				? 1
				: undefined;
		const diedYear =
			tid === PLAYER.RETIRED && bio.diedYear <= season
				? bio.diedYear
				: undefined;

		let retiredYear;
		if (ratings.retiredUntil !== undefined) {
			const lastNonRetiredSeason = allRatings.findLast(
				(row) => row.season < ratings.season && row.retiredUntil === undefined,
			);
			if (lastNonRetiredSeason) {
				retiredYear = lastNonRetiredSeason.season;
			} else {
				// Maybe only one ratings row was passed, so we don't have full history - well, it was sometime before this year!
				retiredYear = ratings.season - 1;
			}
		} else {
			retiredYear = tid === PLAYER.RETIRED ? ratings.season : Infinity;
		}

		pid += 1;

		const p = {
			pid,
			name,
			pos: bio.pos,
			college: bio.college,
			born: {
				year: bornYear,
				loc: bio.country,
			},
			diedYear,
			weight: bio.weight,
			hgt: bio.height,
			tid,
			imgURL: hasPhoto.includes(ratings.slug)
				? `/img/players/${ratings.slug}.jpg`
				: "/img/blank-face.png",
			real: true,
			draft,
			ratings: processedRatings as NonEmptyArray<OnlyRatings>,
			stats,
			injury: undefined as PlayerInjury | undefined,
			contract,
			salaries,
			awards,
			jerseyNumber,
			hof,
			retiredYear,
			srID: ratings.slug,
		};

		if (!p.hof) {
			delete p.hof;
		}

		return p;
	};
};

export default formatPlayerFactory;
